import axios from 'axios';
import { supabase } from './supabaseClient';

// Decide API base URL based on environment
// - Use VITE_API_BASE_URL from .env when set (required when API is on different domain, e.g. webready.app + Railway)
// - In production fallback: same-origin '/api'
// - In development: default to local backend
export const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL 
  || (import.meta.env.PROD ? '/api' : 'http://localhost:5000/api');

// Helper function to get schema from environment
// Reads directly from .env file via Vite's import.meta.env
// In Vite, only variables prefixed with VITE_ are exposed to the browser
export const getSchemaHeader = () => {
  // Read exactly from .env file - VITE_DB_SCHEMA is defined there
  const schema = import.meta.env.VITE_DB_SCHEMA;
  
  if (!schema) {
    console.warn('⚠️ [getSchemaHeader] VITE_DB_SCHEMA is not defined in .env file. Request will continue without schema header, backend will use fallback.');
    // Return null to indicate no schema header should be added
    // Backend middleware will use fallback for public routes
    return null;
  }
  
  /* console.log('🏢 [getSchemaHeader] Schema from .env:', {
    VITE_DB_SCHEMA: schema,
    source: '.env file'
  }); */
  
  return { 'X-DB-Schema': schema };
};

// Helper to get SendGrid API key header (same pattern as X-DB-Schema)
export const getSendGridApiKeyHeader = () => {
  const key = import.meta.env.VITE_SENDGRID_API_KEY;
  if (!key) {
    console.warn('⚠️ [getSendGridApiKeyHeader] VITE_SENDGRID_API_KEY is not defined in .env. Email sending may be skipped on backend.');
    return null;
  }
  return { 'X-SendGrid-Api-Key': key };
};

/**
 * Determines if a route is a dashboard route
 * Dashboard routes require no-cache headers to prevent stale data
 */
function isDashboardRoute(url: string): boolean {
  // Check if URL contains dashboard path
  if (url.includes('/dashboard/') || url.includes('/api/')) {
    // For API routes, check if we're in dashboard context
    if (typeof window !== 'undefined') {
      return window.location.pathname.startsWith('/dashboard');
    }
    // If window is not available (SSR), assume dashboard if URL contains /api/
    // This is conservative - better to disable cache than serve stale data
    return url.includes('/api/');
  }
  return false;
}

// Wrapper for fetch() that automatically adds X-DB-Schema header
export const fetchWithSchema = async (url: string, options: RequestInit = {}) => {
  // Build full URL if needed
  // If URL already starts with /api, remove it to avoid duplication
  let normalizedUrl = url.startsWith('/api') ? url.substring(4) : url;
  let fullUrl = url.startsWith('http') ? url : `${API_BASE_URL}${normalizedUrl.startsWith('/') ? '' : '/'}${normalizedUrl}`;
  
  // Check if body is FormData - if so, don't set Content-Type header (browser will set it with boundary)
  const isFormData = options.body instanceof FormData;
  
  const headers = new Headers(options.headers);
  
  // For FormData, remove Content-Type if it was set - browser needs to set it with boundary
  if (isFormData && headers.has('Content-Type')) {
    headers.delete('Content-Type');
  }
  
  // Check if schema header is already provided in options.headers (for override)
  const existingSchemaHeader = headers.get('X-DB-Schema');
  
  // Only add schema header from env if not already provided
  let finalSchema: string | null = null;
  if (!existingSchemaHeader) {
    const schemaHeader = getSchemaHeader();
    finalSchema = schemaHeader?.['X-DB-Schema'] || null;
    if (finalSchema) {
      headers.set('X-DB-Schema', finalSchema);
    }
  } else {
    finalSchema = existingSchemaHeader;
  }

  // Add SendGrid API key header (same pattern as X-DB-Schema)
  const sendGridHeader = getSendGridApiKeyHeader();
  if (sendGridHeader?.['X-SendGrid-Api-Key']) {
    headers.set('X-SendGrid-Api-Key', sendGridHeader['X-SendGrid-Api-Key']);
  }
  
  // Add schema as query parameter for service worker cache isolation
  // Service worker can't access headers, so we include it in URL
  if (finalSchema) {
    try {
      const urlObj = new URL(fullUrl);
      // Only add if not already present
      if (!urlObj.searchParams.has('schema')) {
        urlObj.searchParams.set('schema', finalSchema);
        fullUrl = urlObj.toString();
      }
    } catch (e) {
      // If URL parsing fails, continue without query parameter
      console.warn('Failed to add schema query parameter to URL:', e);
    }
  }
  
  // Debug logging
  /* console.log('🔍 [fetchWithSchema] Request:', {
    url: fullUrl,
    schema: finalSchema || '(none - backend will use fallback)',
    hasAuth: false // Will be set below
  }); */
  
  // Also add auth token if available
  let hasAuth = false;
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.access_token) {
      headers.set('Authorization', `Bearer ${session.access_token}`);
      hasAuth = true;
      // console.log('🔍 [fetchWithSchema] Added auth token');
    } else {
      // console.warn('⚠️ [fetchWithSchema] No auth token available');
    }
  } catch (error) {
    console.error('💥 [fetchWithSchema] Error getting Supabase session:', error);
  }

  // Add cache-busting headers for dashboard routes
  // This ensures fresh data for authenticated/admin routes
  if (isDashboardRoute(url) || (typeof window !== 'undefined' && window.location.pathname.startsWith('/dashboard'))) {
    headers.set('Cache-Control', 'no-cache');
    headers.set('X-No-Cache', 'true');
  }
  
  /* console.log('🔍 [fetchWithSchema] Final request:', {
    url: fullUrl,
    schema: finalSchema || '(none - backend will use fallback)',
    hasAuth,
    headers: Object.fromEntries(headers.entries())
  }); */
  
  return fetch(fullUrl, {
    ...options,
    headers,
    credentials: 'include',
  });
};

// Lightweight request helper compatible with queryClient.ts apiRequest signature
export async function apiRequestWithSchema(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<Response> {
  const schemaHeader = getSchemaHeader();
  const headers: HeadersInit = data ? { "Content-Type": "application/json" } : {};
  
  // Only add schema header if it exists
  if (schemaHeader?.['X-DB-Schema']) {
    headers['X-DB-Schema'] = schemaHeader['X-DB-Schema'];
  }
  const sendGridHeader = getSendGridApiKeyHeader();
  if (sendGridHeader?.['X-SendGrid-Api-Key']) {
    headers['X-SendGrid-Api-Key'] = sendGridHeader['X-SendGrid-Api-Key'];
  }
  
  // Also add auth token if available
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.access_token) {
      headers['Authorization'] = `Bearer ${session.access_token}`;
    }
  } catch (error) {
    console.error('💥 [apiRequestWithSchema] Error getting Supabase session:', error);
  }

  // Add cache-busting headers for dashboard routes
  if (isDashboardRoute(url) || (typeof window !== 'undefined' && window.location.pathname.startsWith('/dashboard'))) {
    headers['Cache-Control'] = 'no-cache';
    headers['X-No-Cache'] = 'true';
  }

  // Build full URL if needed
  // If URL already starts with /api, remove it to avoid duplication
  let normalizedUrl = url.startsWith('/api') ? url.substring(4) : url;
  const fullUrl = url.startsWith('http') ? url : `${API_BASE_URL}${normalizedUrl.startsWith('/') ? '' : '/'}${normalizedUrl}`;
  
  return fetch(fullUrl, {
    method,
    headers,
    body: data ? JSON.stringify(data) : undefined,
    credentials: "include",
  });
}

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Keep for compatibility
});

// Request interceptor to add JWT token from Supabase Auth and set credentials
api.interceptors.request.use(
  async (config) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (session?.access_token) {
        config.headers.Authorization = `Bearer ${session.access_token}`;
      }

      // Add schema header from environment variables (if available)
      const schemaHeader = getSchemaHeader();
      if (schemaHeader?.['X-DB-Schema']) {
        if (config.headers instanceof Headers) {
          config.headers.set('X-DB-Schema', schemaHeader['X-DB-Schema']);
        } else {
          (config.headers as any)['X-DB-Schema'] = schemaHeader['X-DB-Schema'];
        }
      }
      const sendGridHeader = getSendGridApiKeyHeader();
      if (sendGridHeader?.['X-SendGrid-Api-Key']) {
        if (config.headers instanceof Headers) {
          config.headers.set('X-SendGrid-Api-Key', sendGridHeader['X-SendGrid-Api-Key']);
        } else {
          (config.headers as any)['X-SendGrid-Api-Key'] = sendGridHeader['X-SendGrid-Api-Key'];
        }
      }

      // Add cache-busting headers for dashboard routes
      const url = config.url || '';
      const fullUrl = url.startsWith('http') ? url : `${config.baseURL || API_BASE_URL}${url}`;
      if (isDashboardRoute(fullUrl) || (typeof window !== 'undefined' && window.location.pathname.startsWith('/dashboard'))) {
        if (config.headers instanceof Headers) {
          config.headers.set('Cache-Control', 'no-cache');
          config.headers.set('X-No-Cache', 'true');
        } else {
          (config.headers as any)['Cache-Control'] = 'no-cache';
          (config.headers as any)['X-No-Cache'] = 'true';
        }
      }

      // Set withCredentials to true to send cookies with requests
      config.withCredentials = true;

    } catch (error) {
      console.error('💥 [API Interceptor] Error getting Supabase session:', error);
    }

    return config;
  },
  (error) => {
    console.error('💥 [API Interceptor] Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    
    const originalRequest = error.config;

    // If we get a 401 and haven't already tried to refresh
    if (error.response?.status === 401 && !originalRequest._retry) {

      originalRequest._retry = true;

      try {
        // Try to refresh the session
        const { data: { session }, error: refreshError } = await supabase.auth.refreshSession();
        
        if (refreshError) {

          // Refresh failed, but don't redirect for public routes
          // Only redirect for dashboard/protected routes
          const url = originalRequest.url || '';
          const isPublicRoute = isPublicApiRoute(url);
          
          if (!isPublicRoute) {

            // Only redirect to login for protected routes
            await supabase.auth.signOut();
            window.location.href = '/login';
          }
          
          return Promise.reject(error);
        }

        if (session?.access_token) {

          // Update the failed request with new token
          originalRequest.headers.Authorization = `Bearer ${session.access_token}`;
          return api(originalRequest);
        }
      } catch (refreshError) {
        console.error('💥 [API Response] Error refreshing session:', refreshError);
        
        // Only redirect for protected routes
        const url = originalRequest.url || '';
        const isPublicRoute = isPublicApiRoute(url);
        
        if (!isPublicRoute) {

          // Only redirect to login for protected routes
          await supabase.auth.signOut();
          window.location.href = '/login';
        }
      }
    }

    return Promise.reject(error);
  }
);

// Helper function to determine if a route is public
function isPublicApiRoute(url: string): boolean {
  // Normalize URL - remove base URL if present
  const normalizedUrl = url.replace(/^https?:\/\/[^/]+/, '');
  
  const publicRoutes = [
    '/api/auth/register',
    '/api/auth/login',
    '/api/auth/refresh',
    '/api/auth/forgot-password',
    '/api/auth/reset-password',
    '/api/auth/health',
    '/api/home-sections',
    '/api/v1/services',
    '/api/testimonials',
    '/api/blog-posts',  // This covers /api/blog-posts, /api/blog-posts/slug/..., etc.
    '/api/static-pages/public',
    '/api/static-pages/main',
    '/api/static-pages/nav',
    '/api/static-pages/debug',
    '/api/static-pages/by-slug',
    '/api/gallery',
    '/api/banners',
    '/api/hero',
    '/api/hero/all',
    '/hero/all',  // Add without /api prefix for direct calls
    '/api/app-settings',
    '/api/api-keys',
    '/api/test',
    '/api/health-check',
    '/api/ai/health',
    '/api/employees/public',
    '/api/employees/',  // Public employee by ID endpoint (/api/employees/:id/public)
    '/api/blog-comments/public',  // Public blog comments
    '/api/blog-comments/',  // Blog comment reactions (public read)
  ];

  // Special case for users route - only public for role=employee&active=true
  if (normalizedUrl.startsWith('/api/users') && normalizedUrl.includes('role=employee') && normalizedUrl.includes('active=true')) {
    return true;
  }

  // Check if the normalized URL starts with any public route
  return publicRoutes.some(route => normalizedUrl.startsWith(route));
}

export default api;
